import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchBusinessUnitsAPI,
  fetchFieldsAPI,
  fetchMethodsOfProductionAPI,
  fetchFilteredAssetList,
  FilteredAssetListPayload,
  fetchPCBusinessUnitsAPI,
  fetchPCFieldsAPI,
  fetchPCAreasAPI,
  fetchPCWellGroups,
  fetchPCWellDetails,
} from '../../services/wellFilterService';
import { WellFilterAPIResponse } from '../../types/WellFilterTypes';
import { fetchServerFilteredWellSummary, WellSummaryApiResponse } from './wellFilterThunks'; // NEW import

type SectionFilter = { section: string; value: string; id?: number | string };

interface PCBusinessUnit {
  BusinessUnitId: number;
  Name: string;
}

interface PCField {
  FieldId: number;
  AreaId: number | null;
  AreaName: string | null;
  Name: string;
}

interface PCArea {
  AreaId: number;
  BusinessUnitId: number;
  BusinessUnitName: string;
  Name: string;
}

interface PCWellGroups {
  Name: string;
  WellGroupId: number;
}

interface WellFiltersState {
  businessUnits: string[];
  fields: string[];
  methodsOfProduction: string[];
  hasSelectedFilters: boolean;
  selectedFilters: SectionFilter[];
  selectedFilterType: 'all' | 'groups' | 'watchlist';
  // PC filter selections
  PCSelectedFilters: SectionFilter[];
  PCSelectedFilterType: 'all' | 'groups' | 'watchlist';

  // NEW server-filtered fields
  serverFilteredWellSummary: WellSummaryApiResponse | null;
  serverFilteredLoading: boolean;
  serverFilteredError: string | null;
  filteredAssetList: any[];
  filteredAssetListLoading: boolean;
  filteredAssetListError: string | null;

  //PC filters
  PCBusinessUnits: PCBusinessUnit[];
  PCFields: PCField[];
  PCAreas: PCArea[];
  PCWellGroups: PCWellGroups[];

  PCSelectedAssets: string[];
  PCWellDetails: any[];
  PCWellDetailsLoading: boolean;
  PCWellDetailsError: string | null;
  PCWellIds: string;
}

const initialState: WellFiltersState = {
  businessUnits: [],
  fields: [],
  methodsOfProduction: [],
  hasSelectedFilters: false,
  selectedFilters: [],
  selectedFilterType: 'all',
  // PC filter selections
  PCSelectedFilters: [],
  PCSelectedFilterType: 'all',
  PCSelectedAssets: [],
  PCWellDetails: [],
  PCWellDetailsLoading: false,
  PCWellDetailsError: null,
  PCWellIds: '',
  serverFilteredWellSummary: null,
  serverFilteredLoading: false,
  serverFilteredError: null,
  filteredAssetList: [],
  filteredAssetListLoading: false,
  filteredAssetListError: null,
  PCBusinessUnits: [],
  PCFields: [],
  PCAreas: [],
  PCWellGroups: [],
};

export const fetchBusinessUnits = createAsyncThunk<WellFilterAPIResponse>(
  'wellFilters/fetchBusinessUnits',
  async () => {
    const res = await fetchBusinessUnitsAPI();
    return res;
  },
);

export const fetchFields = createAsyncThunk<WellFilterAPIResponse>('wellFilters/fetchFields', async () => {
  const res = await fetchFieldsAPI();
  return res;
});

export const fetchMethodsOfProduction = createAsyncThunk<WellFilterAPIResponse>(
  'wellFilters/fetchMethodsOfProduction',
  async () => {
    const res = await fetchMethodsOfProductionAPI();
    return res;
  },
);

export const fetchFilteredAssets = createAsyncThunk(
  'wellFilters/fetchFilteredAssets',
  async ({ selectedFilters, groupNames = [] }: { selectedFilters: SectionFilter[]; groupNames?: string[] }) => {
    // Transform selectedFilters to FilteredAssetListPayload format
    const businessUnits = selectedFilters
      .filter((filter) => filter.section === 'Business unit')
      .map((filter) => filter.value);

    const fields = selectedFilters.filter((filter) => filter.section === 'Field').map((filter) => filter.value);

    const methodsOfProduction = selectedFilters
      .filter((filter) => filter.section === 'Method of production')
      .map((filter) => filter.value);

    const payload: FilteredAssetListPayload = {
      groupNames,
      businessUnits,
      fields,
      methodsOfProduction,
    };

    const res = await fetchFilteredAssetList(payload);
    return res;
  },
);

export const fetchPCWellDetailsThunk = createAsyncThunk(
  'wellFilters/fetchPCWellDetails',
  async (assetGuids: string[]) => {
    const res = await fetchPCWellDetails(assetGuids);
    return res;
  },
);

const WellFiltersSlice = createSlice({
  name: 'wellFilters',
  initialState,
  reducers: {
    setHasSelectedFilters(state, action: PayloadAction<boolean>) {
      state.hasSelectedFilters = action.payload;
    },
    setSelectedFilters(state, action: PayloadAction<SectionFilter[]>) {
      state.selectedFilters = action.payload;
    },
    setSelectedFilterType(state, action: PayloadAction<'all' | 'groups' | 'watchlist'>) {
      state.selectedFilterType = action.payload;
    },
    removeFilter(state, action: PayloadAction<SectionFilter | 'ALL'>) {
      if (action.payload === 'ALL') {
        state.selectedFilters = [];
      } else {
        state.selectedFilters = state.selectedFilters.filter(
          (f) =>
            !(
              f.section === (action.payload as SectionFilter).section &&
              f.value === (action.payload as SectionFilter).value
            ),
        );
      }
    },
    // PC filter actions
    setPCSelectedFilters(state, action: PayloadAction<SectionFilter[]>) {
      state.PCSelectedFilters = action.payload;
    },
    setPCSelectedFilterType(state, action: PayloadAction<'all' | 'groups' | 'watchlist'>) {
      state.PCSelectedFilterType = action.payload;
    },
    removePCFilter(state, action: PayloadAction<SectionFilter | 'ALL'>) {
      if (action.payload === 'ALL') {
        state.PCSelectedFilters = [];
      } else {
        state.PCSelectedFilters = state.PCSelectedFilters.filter(
          (f) =>
            !(
              f.section === (action.payload as SectionFilter).section &&
              f.value === (action.payload as SectionFilter).value
            ),
        );
      }
    },
    // PC asset selection actions
    setPCSelectedAssets(state, action: PayloadAction<string[]>) {
      state.PCSelectedAssets = action.payload;
    },
    addPCSelectedAsset(state, action: PayloadAction<string>) {
      if (!state.PCSelectedAssets.includes(action.payload)) {
        state.PCSelectedAssets.push(action.payload);
      }
    },
    removePCSelectedAsset(state, action: PayloadAction<string>) {
      state.PCSelectedAssets = state.PCSelectedAssets.filter((assetId) => assetId !== action.payload);
    },
    clearPCSelectedAssets(state) {
      state.PCSelectedAssets = [];
    },
    setPCWellIds(state, action: PayloadAction<string>) {
      state.PCWellIds = action.payload;
    },
    clearServerFilteredSummary(state) {
      state.serverFilteredWellSummary = null;
      state.serverFilteredError = null;
      state.serverFilteredLoading = false;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessUnits.fulfilled, (state, action) => {
        state.businessUnits = action.payload.values || [];
      })
      .addCase(fetchFields.fulfilled, (state, action) => {
        state.fields = action.payload.values || [];
      })
      .addCase(fetchMethodsOfProduction.fulfilled, (state, action) => {
        state.methodsOfProduction = action.payload.values || [];
      })

      // server-filtered cases
      .addCase(fetchServerFilteredWellSummary.pending, (state) => {
        state.serverFilteredLoading = true;
        state.serverFilteredError = null;
      })
      .addCase(fetchServerFilteredWellSummary.fulfilled, (state, action: PayloadAction<WellSummaryApiResponse>) => {
        state.serverFilteredLoading = false;
        state.serverFilteredWellSummary = action.payload;
        state.serverFilteredError = null;
      })
      .addCase(fetchServerFilteredWellSummary.rejected, (state, action) => {
        state.serverFilteredLoading = false;
        state.serverFilteredError = action.payload ?? 'Error fetching server filtered summary';
      })
      .addCase(fetchFilteredAssets.pending, (state) => {
        state.filteredAssetListLoading = true;
        state.filteredAssetListError = null;
      })
      .addCase(fetchFilteredAssets.fulfilled, (state, action) => {
        state.filteredAssetListLoading = false;
        state.filteredAssetList = action.payload || [];
        state.filteredAssetListError = null;
      })
      .addCase(fetchFilteredAssets.rejected, (state, action) => {
        state.filteredAssetListLoading = false;
        state.filteredAssetListError = action.error.message || 'Failed to fetch filtered assets';
      })
      .addCase(fetchPCBusinessUnitsAPI.fulfilled, (state, action) => {
        state.PCBusinessUnits = action.payload || [];
      })
      .addCase(fetchPCFieldsAPI.fulfilled, (state, action) => {
        state.PCFields = action.payload || [];
      })
      .addCase(fetchPCAreasAPI.fulfilled, (state, action) => {
        state.PCAreas = action.payload || [];
      })
      .addCase(fetchPCWellGroups.fulfilled, (state, action) => {
        state.PCWellGroups = action.payload || [];
      })
      .addCase(fetchPCWellDetailsThunk.pending, (state) => {
        state.PCWellDetailsLoading = true;
        state.PCWellDetailsError = null;
      })
      .addCase(fetchPCWellDetailsThunk.fulfilled, (state, action) => {
        state.PCWellDetailsLoading = false;
        state.PCWellDetails = action.payload || [];
        state.PCWellDetailsError = null;

        // Extract well IDs
        const wellIds = (action.payload || [])
          .map((well: any) => well.WellId)
          .filter((id: any) => id !== undefined && id !== null)
          .join(',');
        state.PCWellIds = wellIds;
      })
      .addCase(fetchPCWellDetailsThunk.rejected, (state, action) => {
        state.PCWellDetailsLoading = false;
        state.PCWellDetailsError = action.error.message || 'Failed to fetch PC well details';
      });
  },
});

export const {
  setHasSelectedFilters,
  setSelectedFilters,
  setSelectedFilterType,
  removeFilter,
  setPCSelectedFilters,
  setPCSelectedFilterType,
  removePCFilter,
  setPCSelectedAssets,
  addPCSelectedAsset,
  removePCSelectedAsset,
  clearPCSelectedAssets,
  setPCWellIds,
  clearServerFilteredSummary,
} = WellFiltersSlice.actions;
export default WellFiltersSlice.reducer;
